import React, { useMemo } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import HomeV1 from "./components/home-v1";
import { useOffline } from "./hooks/useOffline";
import ScrollToTop from "./components/ScrollToTop";
import { useAuth } from "./hooks/useAuth";

import MyAccount from "./pages/MyAccount";
import Auth from "./pages/Auth";
import Products from "./pages/Products";
import ProductsWithVideo from "./pages/ProductsWithVideo";
import ContextProvider from "./util/ContextProvider";
import LayoutBanner from "./components/LayoutBanner";
import Cart from "./pages/Cart";
import CheckoutLayout from "./pages/CheckoutLayout";
import { Route, Routes } from "react-router";
import MyContactTerra from "./pages/MyAccount/components/MyContactTerra";
import MyAccountCollab from "./pages/MyAccount/components/MyAccountColab";
import MyAccountOrderHistory from "./pages/MyAccount/components/MyAccountOrderHistory";
import MyAccountPaymentDelivery from "./pages/MyAccount/components/MyAccountPaymentDelivery";
import ChangePasswordForm from "./pages/MyAccount/components/ChangePasswordForm";
import MyAccountLogout from "./pages/MyAccount/components/MyAccountLogout";
import MyAccountCollabDetails from "./pages/MyAccount/components/MyAccountCollabDetails";
import MyClients from "./pages/MyClients";
import MyClientsCollaborators from "./pages/MyClients/MyClientsCollaborators";
import VerificationCodeForm from "./pages/MyAccount/components/VerificationCodeForm";
import RequestCodeForm from "./pages/MyAccount/components/RequestCodeForm";
import MyAccountDetailsOrderHistory from "./pages/MyAccount/components/MyAccountDetailsOrderHistory";
import MyAccountUserProfile from "./pages/MyAccount/components/MyAccountUserProfile";
import MyAccountDetailsCompany from "./pages/MyAccount/components/MyAccountDetailsCompany";
import MyAccountBuyerDetails from "./pages/MyAccount/components/MyAccountBuyerDetails";
import { Navigate } from "react-router-dom";
import FinalizationCart from "./pages/FinalizationCart.js";
import ProductDetail from "./pages/Product-Detail/index.js";
import ResetPassword from "./pages/ResetPassword/index.js";
import TermsAndPrivacy from "./pages/TermsAndPrivacy";
import ComponentProvider from "./providers/ComponentProvider";
import Loader from "./components/Loader";

export default function Root() {
  const { isLoggedIn, userInfo, user, isCheckingAuth } = useAuth();
  const isOffline = useOffline();

  // Otimizado com useMemo para evitar recálculos desnecessários
  const isLogged = useMemo(() => isLoggedIn, [isLoggedIn]);
  // Usa userInfo se disponível, senão usa user como fallback
  const isUserTerra = useMemo(
    () => Boolean(userInfo?.is_user_terra ?? user?.is_user_terra ?? false),
    [userInfo?.is_user_terra, user?.is_user_terra]
  );


  const PrivateRoute = ({ element, isLogged }) => {
    if (isCheckingAuth) {
      return <Loader type="pulse" size="medium" fullScreen />;
    }
    return isLogged ? (
      element
    ) : (
      <Navigate
        to="/autenticacao"
        replace
        state={{ from: window.location.pathname }}
      />
    );
  };


  const Layout = ({ children }) => {
    return <LayoutBanner>{children}</LayoutBanner>;
  };

  return (
    <ComponentProvider
      isLogged={isLogged}
      isOffline={isOffline}
      userUid={user?.uid}
      isCheckingAuth={isCheckingAuth}
    >
      <ScrollToTop />
      <Routes>
        <Route exact path="/" element={<HomeV1 />} />
        <Route
          exact
          path="/autenticacao"
          element={
            <Layout>
              <Auth />
            </Layout>
          }
        />
        <Route
          exact
          path="/redefinir-senha"
          element={
            <Layout>
              <ResetPassword />
            </Layout>
          }
        />
        <Route
          exact
          path="/termos-e-politicas"
          element={
            <Layout>
              <TermsAndPrivacy />
            </Layout>
          }
        />
        {
          <Route
            exact
            path="/produtos"
            element={
              <Layout>
                <Products />
              </Layout>
            }
          />
        }
        <Route
          exact
          path="/videos"
          element={
            <Layout>
              <ProductsWithVideo />
            </Layout>
          }
        />
        <Route
          exact
          path="/produto/:id"
          element={
            <PrivateRoute
              element={
                <Layout>
                  <ProductDetail />
                </Layout>
              }
              isLogged={isLogged}
            />
          }
        />
        <Route
          path="/carrinho"
          element={
            <PrivateRoute
              element={
                <Layout>
                  <Cart />
                </Layout>
              }
              isLogged={isLogged}
            />
          }
        />
        <Route
          path="/pedido-finalizado"
          element={
            <PrivateRoute
              element={
                <Layout>
                  <FinalizationCart />
                </Layout>
              }
              isLogged={isLogged}
            />
          }
        />
        <Route
          path="/finalizar-pedido"
          element={
            <PrivateRoute
              element={
                <Layout>
                  <CheckoutLayout />
                </Layout>
              }
              isLogged={isLogged}
            />
          }
        />
        <Route
          exact
          path="/minha-conta"
          element={
            <PrivateRoute
              element={
                <Layout>
                  <MyAccount />
                </Layout>
              }
              isLogged={isLogged && !isUserTerra}
            />
          }
        >
          <Route path="perfil" element={<MyAccountUserProfile />} />

          <Route
            path="perfil/detalhes-empresa"
            element={<MyAccountDetailsCompany />}
          />
          <Route
            path="perfil/detalhes-empresa/empresa/:companyId/compradores/:buyerIdUser"
            element={<MyAccountBuyerDetails />}
          />
          <Route path="contato" element={<MyContactTerra />} />
          <Route path="colaboradores" element={<MyAccountCollab />} />
          <Route
            path="colaboradores/detalhes"
            element={<MyAccountCollabDetails />}
          />
          <Route path="historico-pedidos" element={<MyAccountOrderHistory />} />
          <Route
            path="pagamento-entrega"
            element={<MyAccountPaymentDelivery />}
          />
          <Route
            path="alterar-senha/verificar-codigo/nova-senha"
            element={<ChangePasswordForm />}
          />
          <Route path="alterar-senha" element={<RequestCodeForm />} />
          <Route
            path="alterar-senha/verificar-codigo"
            element={<VerificationCodeForm />}
          />
          <Route path="sair" element={<MyAccountLogout />} />
        </Route>
        <Route
          path="/detalhes-pedido"
          element={
            <PrivateRoute
              element={
                <Layout>
                  <MyAccountDetailsOrderHistory />
                </Layout>
              }
              isLogged={isLogged}
            />
          }
        />
        <Route
          exact
          path="/meus-clientes"
          element={
            <PrivateRoute
              element={
                <Layout>
                  <MyClients />
                </Layout>
              }
              isLogged={isLogged && isUserTerra}
            />
          }
        />
        <Route
          exact
          path="/meus-clientes/:myClientsCollabId"
          element={
            <PrivateRoute
              element={
                <Layout>
                  <MyClientsCollaborators />
                </Layout>
              }
              isLogged={isLogged && isUserTerra}
            />
          }
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </ComponentProvider>
  );
}

ReactDOM.render(
  <BrowserRouter basename="/">
    <ContextProvider>
      <Root />
    </ContextProvider>
  </BrowserRouter>,
  document.getElementById("terra")
);
